import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const PRODUCTION_HOSTNAME = "viraluj.pl";

function isTurnstileEnabled(): boolean {
  return !!TURNSTILE_SITE_KEY && window.location.hostname === PRODUCTION_HOSTNAME;
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const SCRIPT_ID = "turnstile-script";

const TurnstileWidget = ({ onVerify }: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enabled] = useState(isTurnstileEnabled);

  useEffect(() => {
    if (!enabled) {
      onVerify("disabled");
      setIsLoading(false);
      return;
    }

    let scriptLoadTimeout: ReturnType<typeof setTimeout>;
    let turnstileCheckInterval: ReturnType<typeof setInterval>;

    const initTurnstile = () => {
      try {
        if (!containerRef.current || !window.turnstile) return;

        if (!TURNSTILE_SITE_KEY) {
          setError("System weryfikacji nie jest skonfigurowany.");
          setIsLoading(false);
          return;
        }

        // Remove existing widget before re-rendering
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
            widgetIdRef.current = undefined;
          } catch (err) {
            // Ignore removal errors
          }
        }

        containerRef.current.innerHTML = "";
        setError(null);

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "light",
          language: "pl",
          "refresh-expired": "auto",
          callback: (token: string) => {
            setError(null);
            if (token && token.length > 0) {
              onVerify(token);
            } else {
              setError("Weryfikacja nie powiodła się. Odśwież stronę.");
              onVerify("");
            }
          },
          "error-callback": () => {
            setError("Weryfikacja nie powiodła się. Odśwież stronę.");
            onVerify("");
          },
          "expired-callback": () => {
            setError("Weryfikacja wygasła. Odśwież stronę.");
            onVerify("");
          },
        });

        setIsLoading(false);
      } catch {
        setError("Nie udało się załadować weryfikacji.");
        setIsLoading(false);
        onVerify("");
      }
    };

    const loadTurnstile = () => {
      try {
        setError(null);
        setIsLoading(true);

        // Always set up a polling interval that waits for both
        // window.turnstile AND containerRef to be ready.
        // This handles: script already loaded, container not yet in DOM,
        // and switching between login/register forms.
        turnstileCheckInterval = setInterval(() => {
          if (window.turnstile && containerRef.current && !widgetIdRef.current) {
            clearInterval(turnstileCheckInterval);
            clearTimeout(scriptLoadTimeout);
            initTurnstile();
          }
        }, 100);

        scriptLoadTimeout = setTimeout(() => {
          clearInterval(turnstileCheckInterval);
          setError("Ładowanie weryfikacji trwa zbyt długo. Odśwież stronę.");
          setIsLoading(false);
        }, 10000);

        // If script already exists, just wait for the interval above
        const existingScript = document.getElementById(SCRIPT_ID);
        if (existingScript) return;

        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
        script.async = true;
        script.defer = true;

        script.onerror = () => {
          clearInterval(turnstileCheckInterval);
          setError("Nie udało się załadować weryfikacji. Odśwież stronę.");
          setIsLoading(false);
        };

        window.onTurnstileLoad = () => {
          // The interval will pick it up, but also try immediately
          if (containerRef.current && !widgetIdRef.current) {
            clearInterval(turnstileCheckInterval);
            clearTimeout(scriptLoadTimeout);
            initTurnstile();
          }
        };

        document.head.appendChild(script);
      } catch {
        setError("Wystąpił błąd podczas ładowania weryfikacji.");
        setIsLoading(false);
      }
    };

    if (!widgetIdRef.current) {
      loadTurnstile();
    }

    return () => {
      clearTimeout(scriptLoadTimeout);
      clearInterval(turnstileCheckInterval);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = undefined;
        } catch {
          // Ignore cleanup errors
        }
      }
    };
  }, []); // Stable deps — no re-render loops

  if (!enabled) return null;

  return (
    <div className="flex flex-col items-center justify-center">
      <div
        ref={containerRef}
        className={`transition-opacity ${isLoading ? "opacity-0" : "opacity-100"}`}
      />
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Ładowanie weryfikacji...
        </div>
      )}
      {error && (
        <p className="text-sm text-destructive mt-2">{error}</p>
      )}
    </div>
  );
};

export { isTurnstileEnabled };
export default TurnstileWidget;
